import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, API_BASE } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { Upload, Loader2, CheckCircle2, AlertCircle, Image as ImageIcon, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);

  // Icon manager
  const [search, setSearch] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [uploadingId, setUploadingId] = useState(null);
  const [iconInputs, setIconInputs] = useState({});

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login'); return; }
    // Show explicit message for non-admins instead of silent redirect.
  }, [user, authLoading, navigate]);

  const handleUpload = async (e) => {
    e.preventDefault();
    const file = fileRef.current?.files?.[0];
    if (!file) { toast.error('Choose an .xlsx file first'); return; }
    setUploading(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_BASE}/admin/upload-excel`, {
        method: 'POST',
        credentials: 'include',
        body: form,
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Upload failed');
      const data = await res.json();
      setResult(data);
      toast.success(`Imported ${data.inserted_or_updated} rows across ${data.sheets_processed.length} sheets`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    setSearching(true);
    try {
      const { data } = await api.get(`/characters?q=${encodeURIComponent(search)}&limit=12`);
      setResults(data.items);
      const inputs = {};
      data.items.forEach((c) => { inputs[c.id] = c.image_url || ''; });
      setIconInputs(inputs);
    } finally {
      setSearching(false);
    }
  };

  const handleSaveIcon = async (id) => {
    setSavingId(id);
    try {
      const { data } = await api.patch(`/admin/characters/${id}/image`, { image_url: iconInputs[id] || null });
      setResults((r) => r.map((c) => c.id === id ? data : c));
      toast.success('Icon updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Update failed');
    } finally {
      setSavingId(null);
    }
  };

  const handleFileUpload = async (id, file) => {
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error('Image too large (max 5 MB)'); return; }
    setUploadingId(id);
    try {
      const form = new FormData();
      form.append('file', file);
      const res = await fetch(`${API_BASE}/admin/characters/${id}/upload-icon`, {
        method: 'POST', credentials: 'include', body: form,
      });
      if (!res.ok) throw new Error((await res.json()).detail || 'Upload failed');
      const data = await res.json();
      setResults((r) => r.map((c) => c.id === id ? data : c));
      setIconInputs((s) => ({ ...s, [id]: data.image_url || '' }));
      toast.success('Icon uploaded');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setUploadingId(null);
    }
  };

  if (authLoading || !user) {
    return <div className="flex items-center justify-center py-32"><Loader2 className="w-6 h-6 text-slate-400 animate-spin" /></div>;
  }
  if (!user.is_admin) {
    return (
      <div className="container-page py-24 max-w-xl mx-auto text-center">
        <AlertCircle className="w-10 h-10 text-amber-500 mx-auto mb-4" />
        <h1 className="font-serif-display text-4xl text-slate-900 mb-3">Not an admin account</h1>
        <p className="text-slate-600 mb-6">
          You're signed in as <span className="font-mono text-sm bg-slate-100 px-2 py-0.5 rounded">{user.email}</span>, which isn't on the admin allowlist.
        </p>
        <p className="text-sm text-slate-500 mb-8">
          The admin allowlist is controlled by the <span className="font-mono">ADMIN_EMAILS</span> backend env var. Currently approved: <span className="font-mono">martazk15@yahoo.com</span>.
        </p>
        <div className="flex gap-3 justify-center">
          <button onClick={() => navigate('/')} className="btn-secondary text-sm">Back to directory</button>
          <button
            onClick={async () => { await useAuth; window.location.href = '/login'; }}
            className="btn-primary text-sm"
          >
            Sign in as a different user
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container-page py-12 lg:py-16 max-w-4xl">
      <div className="mb-10">
        <span className="text-xs uppercase tracking-[0.25em] text-[#0F766E] font-semibold">Restricted · Admin</span>
        <h1 className="font-serif-display text-5xl text-slate-900 mt-2">Admin Console</h1>
        <p className="text-slate-600 mt-1">Manage the people database and assign icons.</p>
      </div>

      {/* Excel Upload */}
      <section className="bg-white border border-slate-200 rounded-2xl p-8 mb-8">
        <h2 className="font-serif-display text-3xl text-slate-900 mb-2">Bulk import (.xlsx)</h2>
        <p className="text-sm text-slate-600 mb-6">
          One sheet per book of the Bible. Columns: <span className="font-mono text-xs bg-slate-100 px-1.5 py-0.5 rounded">Character | Where | Major/Minor | Details</span>. Rows are upserted by (book, name) — safe to re-run.
        </p>
        <form onSubmit={handleUpload} className="flex flex-col sm:flex-row gap-3">
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xlsm"
            data-testid="admin-excel-input"
            className="flex-1 text-sm file:mr-3 file:py-2.5 file:px-4 file:rounded-md file:border file:border-slate-200 file:bg-white file:text-slate-700 file:hover:border-[#1E3A8A] file:cursor-pointer text-slate-600"
          />
          <button
            type="submit"
            disabled={uploading}
            data-testid="admin-excel-upload-button"
            className="btn-primary disabled:opacity-60 whitespace-nowrap"
          >
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {uploading ? 'Uploading…' : 'Upload & ingest'}
          </button>
        </form>

        {result && (
          <div className="mt-6 border border-emerald-200 bg-emerald-50 rounded-md p-4 text-sm">
            <div className="flex items-start gap-2 text-emerald-900">
              <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-semibold">Import complete</p>
                <p className="mt-1">
                  {result.inserted_or_updated} rows inserted/updated · {result.sheets_processed.length} sheets · {result.skipped} skipped
                </p>
                <p className="text-xs text-emerald-700 mt-2 font-mono break-all">
                  Sheets: {result.sheets_processed.join(', ')}
                </p>
              </div>
            </div>
          </div>
        )}
      </section>

      {/* Icon manager */}
      <section className="bg-white border border-slate-200 rounded-2xl p-8">
        <h2 className="font-serif-display text-3xl text-slate-900 mb-2">Person images</h2>
        <p className="text-sm text-slate-600 mb-6">
          Upload a PNG/JPG (max 5 MB) <strong>or</strong> paste a publicly-reachable image URL. Cats-&-Soup-style icons recommended.
        </p>

        <form onSubmit={handleSearch} className="flex gap-2 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search person to assign an icon…"
              className="input-base pl-10 py-2.5"
            />
          </div>
          <button type="submit" disabled={searching} className="btn-secondary text-sm">
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
          </button>
        </form>

        {results.length === 0 ? (
          <p className="text-sm text-slate-500 text-center py-8 italic">No results yet — search a person above.</p>
        ) : (
          <div className="space-y-3">
            {results.map((c) => (
              <div key={c.id} className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 border border-slate-200 rounded-lg">
                <div className="w-14 h-14 rounded-md bg-slate-100 border border-slate-200 flex items-center justify-center overflow-hidden flex-shrink-0">
                  {c.image_url ? (
                    <img src={c.image_url} alt={c.name} className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="w-5 h-5 text-slate-400" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-serif-display text-lg text-slate-900 truncate">{c.name}</div>
                  <div className="text-xs text-slate-500 font-mono truncate">{c.book} · {c.scripture_reference}</div>
                </div>
                <input
                  type="url"
                  value={iconInputs[c.id] ?? ''}
                  onChange={(e) => setIconInputs((s) => ({ ...s, [c.id]: e.target.value }))}
                  placeholder="https://…/image.png"
                  className="input-base text-sm py-2 sm:w-72"
                />
                <button
                  onClick={() => handleSaveIcon(c.id)}
                  disabled={savingId === c.id}
                  className="btn-primary text-sm py-2 px-4 whitespace-nowrap"
                >
                  {savingId === c.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save'}
                </button>
              </div>
            ))}
          </div>
        )}
      </section>

      <div className="mt-8 flex items-start gap-2 text-xs text-slate-500 border-l-2 border-slate-200 pl-3">
        <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
        <p>
          Admin access is granted via the <span className="font-mono">ADMIN_EMAILS</span> backend env var.
          To revoke, remove the email and ask the user to sign out / sign back in.
        </p>
      </div>
    </div>
  );
}
